import type { AppRouteQueryType } from '@agentselly/immodossier-shared';
import {
  AppRoute,
  loaderRouteFromAppRoute,
} from '@agentselly/immodossier-shared';
import type { QueryObserverOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { ErrorType } from '~/model/ErrorType';
import { fetchBackend } from '~/helper/fetchBackend.ts';
import { useRouter } from '@tanstack/react-router';
import { useAuth, useUser } from '@clerk/clerk-react';
import {
  shouldAutoRetryAuth,
  clearAuthAutoRetryFlagIfEpisodeOver,
} from '~/helper/authAutoRetry.ts';
import { isPublicPath } from '~/helper/authGuard';

export function useLoaderQuery<T extends AppRoute>(
  key: T,
  options: Options<AppRouteQueryType<T>>,
) {
  const router = useRouter();
  const { isLoaded, isSignedIn, getToken } = useAuth();
  // Bind the query cache to the authenticated identity so an anonymous /
  // pre-login error response cached for a guest is never re-used for the
  // newly signed-in session (and vice versa after logout).
  const userId = useUser()?.user?.id || null;
  const isPublic = isPublicPath(location.pathname);

  return useQuery({
    enabled: isLoaded && (isPublic || !!isSignedIn),
    // Keep previous data during route transitions to avoid flicker
    keepPreviousData: true,
    placeholderData: (prev) => prev as any,
    // Make navigation data relatively stable
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryKey: [
      key,
      // Scope by identity: a loader query must never share its cache entry
      // between different sessions/users.
      isPublic ? null : userId,
      // Only include currentRoute in the query key for non-root routes.
      // The root loader data (permissions, handlerUrl, etc.) is mostly static
      // and should not refetch on every client-side navigation. The dossierTitle
      // from the initial load persists in the cache; child route loaders provide
      // fresh address data via usePageTitle.
      key === AppRoute.ROOT ? null : options?.currentRoute,
      options?.params && Object.keys(options?.params).length
        ? options?.params
        : null,
    ].filter(Boolean),
    queryFn: async function () {
      if (key === AppRoute.ROOT && !options?.currentRoute) {
        return null;
      }

      // Pass query parameters to the backend
      const url = new URL(location.href);

      // Use static template path expected by the backend (params are passed via query + x-current-route)
      const loaderPath = loaderRouteFromAppRoute(key);
      url.pathname = `/${
        options?.isDemo ? 'demo-loader' : 'loader'
      }/${loaderPath}`;

      if (isPublic) {
        url.searchParams.delete('contactId');
      }

      for (const [
        paramKey,
        value,
      ] of Object.entries(options?.params || {})) {
        if (isPublic && paramKey === 'contactId') {
          continue;
        }

        if (url.searchParams.has(paramKey)) {
          throw new Error(
            `Key ${paramKey} already exists and is not allowed to be set again`,
          );
        }

        url.searchParams.set(paramKey, value);
      }

      // Await a fresh session token BEFORE the request leaves. Relying only on
      // the Clerk __session cookie races the post-setActive() session commit
      // and can fire a loader request with a still-stale/absent cookie, which
      // the backend then treats as a guest -> 401/403 -> "Unerwarteter Fehler".
      // Sending the token explicitly (ClerkService prefers an Authorization
      // header over the cookie) closes that race.
      const token = isPublic ? null : await getToken();

      if (!isPublic && isSignedIn && !token) {
        // If the user is logged in, but we failed to get a token, the session is
        // not fully ready/propagated yet. Throw a specific auth/retryable error
        // so TanStack Query retries and wait for the token to be issued, instead
        // of firing an anonymous request that will fail with 401/403.
        throw new Error('Auth session not ready (missing token)');
      }

      let response: Response;
      try {
        response = await fetchBackend(url, {
          method: options?.body ? 'POST' : 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'x-current-route': options?.currentRoute || (key as any),
            'x-current-path': location.pathname,
          },
          body: options?.body ? JSON.stringify(options.body) : undefined,
        });
      } catch (err) {
        // A timeout abort (fetchBackend) or network failure must surface as a
        // comprehensible, retryable error — NOT as an opaque AbortError that
        // leaves a blank/white page. Map it to a stable sentinel message the
        // ErrorWidget renders with a "Seite neu laden" action.
        const name = (err as Error)?.name;
        if (name === 'AbortError' || name === 'TimeoutError') {
          throw new Error(ErrorType.BackendTimeout);
        }
        // Network failure (TypeError: Failed to fetch) — same treatment.
        if ((err as TypeError)?.name === 'TypeError') {
          throw new Error(ErrorType.BackendNetwork);
        }
        throw err;
      }

      if (!response.ok) {
        if (response.status === 400) {
          // Throw special error that TanStack Router recognizes as a BadRequest case
          throw new Error(ErrorType.BadRequest);
        }

        if (response.status === 401) {
          // Auto-retry: if the session token wasn't propagated after Clerk OAuth,
          // a single page reload resolves it. Guard ensures at most ONE reload.
          if (shouldAutoRetryAuth(Date.now(), sessionStorage)) {
            window.location.reload();
            return null;
          }

          // Redirect to login page via router so the history stack is preserved
          void router.navigate({ to: '/auth/login', replace: true });

          return null;
        }

        if (response.status === 404) {
          // Throw special error that TanStack Router recognizes as a NotFound case
          throw new Error(ErrorType.NotFound);
        }

        if (response.status === 403) {
          // Auto-retry: same logic as 401 — session token may not be propagated
          if (shouldAutoRetryAuth(Date.now(), sessionStorage)) {
            window.location.reload();
            return null;
          }

          // Throw special error that TanStack Router recognizes as a Forbidden case
          throw new Error(ErrorType.Forbidden);
        }

        if (response.status === 503 || response.status === 504) {
          // Upstream Strapi timeout (504) or unavailable (503): surface as
          // BackendTimeout so TanStack Query retries and ErrorWidget renders
          // "Seite neu laden" instead of NotFoundWidget.
          throw new Error(ErrorType.BackendTimeout);
        }

        console.error(
          'useLoaderQuery Error',
          response.status,
          response.statusText,
          await response.text(),
        );

        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      // Happy path: successful response — clear auto-retry flag only if the
      // current episode has expired. This prevents a parallel 401 loader from
      // having its flag cleared mid-episode, which was the root cause of the
      // infinite reload loop (max 1 reload per 10-minute episode).
      clearAuthAutoRetryFlagIfEpisodeOver(Date.now(), sessionStorage);

      return (await response.json()) as AppRouteQueryType<T>;
    },
    // Provide optional initial data if caller wants to avoid first-load gaps
    initialData: options?.defaultValue,

    retry: (failureCount, error) => {
      if (
        [
          ErrorType.NotFound,
          ErrorType.Forbidden,
          ErrorType.BadRequest,
        ].includes(error?.message as any)
      ) {
        // Do not retry these errors
        return false;
      }

      // Gezielter Retry NUR fuer Auth session not ready, 401, 403 oder Backend Timeout/Network
      const isAuthError =
        error?.message?.includes('401') ||
        error?.message?.includes('403') ||
        error?.message === 'Auth session not ready (missing token)';

      if (isAuthError) {
        // Retry auth errors up to 3 times with standard query backoff
        return failureCount < 3;
      }

      // Default retry behavior for other errors (e.g. backend timeouts or network errors, not global 500s)
      const isNetworkOrTimeout =
        error?.message === ErrorType.BackendTimeout ||
        error?.message === ErrorType.BackendNetwork;

      if (isNetworkOrTimeout) {
        return failureCount < 3;
      }

      // Do not retry other server errors (like 500 API Error) instantly
      return false;
    },
    refetchInterval: options?.refetchInterval,
  });
}

interface Options<T> {
  readonly params: Record<string, string>;
  readonly isDemo?: boolean;
  readonly body?: any;
  readonly defaultValue?: T;
  readonly refetchInterval?: QueryObserverOptions<any>['refetchInterval'];
  readonly currentRoute?: string;
}

/**
 * True when a loader query has not yet produced data and must be shown as
 * "loading" rather than as an error.
 *
 * A disabled / never-run query (e.g. while Clerk is hydrating or during an
 * auth transition where `enabled` is momentarily false) reports these flags in
 * TanStack Query v5: `isLoading === false`, `isPending === true`,
 * `fetchStatus === 'idle'`, `isError === false` and `data === undefined`.
 * `query.isLoading` alone misses this state, which made the root layout fall
 * through to `!query.data` and render the ErrorWidget during login/logout.
 *
 * We detect it via `isPending && fetchStatus === 'idle'` (never ran, and not
 * currently fetching) and treat it as loading. This cannot cause an eternal
 * spinner for a permanently-disabled guest on a protected route: the root
 * route's `beforeLoad` guard redirects such guests to /auth/login before this
 * render decision is ever reached.
 */
export function isLoaderQueryPending(query: {
  readonly isLoading: boolean;
  readonly isPending: boolean;
  readonly fetchStatus: string;
}): boolean {
  return query.isLoading || (query.isPending && query.fetchStatus === 'idle');
}
